import React, { useEffect, useState } from "react";
import { PaperPlaneTilt, Buildings } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState("Thaundagroundshoow@gmail.com");

  useEffect(() => {
    api.get("/public/contact-email").then(({ data }) => {
      if (data?.email) setPrimaryEmail(data.email);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success("Inquiry received", {
        description: data.message || "Thank you — your inquiry has been received.",
        duration: 6000,
      });
      setDone(true);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message, { duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
      data-testid="contact-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 space-y-4">
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase inline-flex items-center gap-2">
            <Buildings size={14} weight="bold" /> Labels & Management
          </p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-balance">
            Got a deal? <span className="text-neon-cyan">Slide in.</span>
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed max-w-md">
            Reps, A&Rs, festival bookers and brand partners — leave a note. Our management team reviews every inquiry and replies from{" "}
            <a className="text-neon-cyan hover:text-neon-cyanHover transition-colors normal-case" href={`mailto:${primaryEmail}`}>
              {primaryEmail}
            </a>
            .
          </p>
          <div className="pt-4 grid grid-cols-1 gap-3 text-xs font-mono uppercase tracking-widest text-zinc-500">
            <div>
              <p className="text-[10px] mb-1">Direct</p>
              <p className="text-zinc-300 normal-case">{primaryEmail}</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 glass rounded-lg p-6 lg:p-8 space-y-4"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">
                Name *
              </label>
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name or label name"
                className="w-full px-3 py-2.5 rounded-md bg-ink-800 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">
                Phone
              </label>
              <input
                value={form.phone}
                onChange={set("phone")}
                placeholder="+1 (555) 000-0000"
                className="w-full px-3 py-2.5 rounded-md bg-ink-800 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                data-testid="contact-phone"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">
              Email *
            </label>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@label.com"
              className="w-full px-3 py-2.5 rounded-md bg-ink-800 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 transition-all"
              data-testid="contact-email"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">
              Comments *
            </label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about the artist you're interested in, the project, deal terms, or anything relevant..."
              className="w-full px-3 py-2.5 rounded-md bg-ink-800 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 resize-y transition-all"
              data-testid="contact-message"
            />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <p className="text-xs text-zinc-500 font-mono">
              {done ? "Inquiry sent — check your inbox for a reply." : "We respect your privacy. No spam, ever."}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-neon-cyan text-black font-semibold text-sm hover:bg-neon-cyanHover transition-all duration-300 shadow-[0_0_24px_rgba(255,138,60,0.3)] disabled:opacity-60"
              data-testid="contact-submit"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {submitting ? "Sending..." : "Send inquiry"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
